import { getStaticData } from '../hooks/useStaticData';

import { CDN_URL } from '@/config/cdn';

const SITE_URL = 'https://owazamartialarts.com';
const LOGO_URL = `${CDN_URL}/images/owaza-logo.png`;

/**
 * Generate LocalBusiness (MartialArtsSchool) schema
 */
export function getLocalBusinessSchema() {
  const businessInfo = getStaticData('businessInfo');
  const testimonials = getStaticData('testimonials') || [];
  const schedule = getStaticData('schedule') || [];

  // Calculate aggregate rating from testimonials
  const ratings = testimonials.filter(t => t.rating);
  const avgRating = ratings.length > 0
    ? (ratings.reduce((sum, t) => sum + t.rating, 0) / ratings.length).toFixed(1)
    : '5.0';

  // Generate opening hours from schedule
  const dayMap = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  // Only count days that have at least one slot with a real program — placeholder
  // slots (e.g. Fri "By Appointment", Sat "By Invitation", Sun "Private") have
  // empty program_name and should NOT be advertised as regular opening hours.
  const openingHours = schedule
    .filter(day => day.time_slots && day.time_slots.some(s => s.program_name && s.program_name.trim()))
    .map(day => {
      const programSlots = day.time_slots.filter(s => s.program_name && s.program_name.trim());
      const firstSlot = programSlots[0];
      const lastSlot = programSlots[programSlots.length - 1];
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": dayMap[day.day.toLowerCase()],
        "opens": firstSlot.start_time,
        "closes": lastSlot.end_time
      };
    });

  return {
    "@context": "https://schema.org",
    // Explicit LocalBusiness in the @type array so Google's review-snippet
    // validator recognizes the parent type for aggregateRating. MartialArtsSchool
    // alone is a valid LocalBusiness subtype, but the Rich Results Test does
    // not always resolve subtype inheritance for review rich results.
    "@type": ["MartialArtsSchool", "LocalBusiness"],
    "@id": `${SITE_URL}/#organization`,
    "name": businessInfo?.name || "Owaza Martial Arts",
    "alternateName": "Owaza Ryu Ju-Jitsu",
    "url": SITE_URL,
    "telephone": businessInfo?.phone || "(352) 428-0086",
    "email": businessInfo?.email || "info@owazamartialarts.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14726 County Line Rd",
      "addressLocality": "Spring Hill",
      "addressRegion": "FL",
      "postalCode": "34610",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 28.4788,
      "longitude": -82.5453
    },
    "image": LOGO_URL,
    "logo": LOGO_URL,
    "priceRange": "$$",
    "areaServed": [
      {
        "@type": "City",
        "name": "Spring Hill",
        "containedInPlace": { "@type": "State", "name": "Florida" }
      },
      {
        "@type": "AdministrativeArea",
        "name": "Hernando County"
      }
    ],
    "hasMap": businessInfo?.google_maps_url || "https://www.google.com/maps/place/Owaza+Ryu+Ju-Jitsu+%2F+Judo+Spring+Hill+FL",
    "sameAs": [
      "https://www.facebook.com/OwazaMartialArts/",
      ...(businessInfo?.google_maps_url ? [businessInfo.google_maps_url] : ["https://www.google.com/maps/place/Owaza+Ryu+Ju-Jitsu+%2F+Judo+Spring+Hill+FL"]),
      ...(businessInfo?.instagram_url ? [businessInfo.instagram_url] : []),
      ...(businessInfo?.youtube_url ? [businessInfo.youtube_url] : []),
    ],
    ...(openingHours.length > 0 && { "openingHoursSpecification": openingHours }),
    ...(ratings.length > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avgRating,
        "reviewCount": ratings.length.toString(),
        "bestRating": "5",
        "worstRating": "1"
      }
    }),
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Authorization",
      "name": "Authorized Provider for Martial Arts, Wellness and Fitness Classes; Martial Arts Supply Vendor",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Step Up For Students",
        "url": "https://www.stepupforstudents.org"
      }
    }
  };
}

/**
 * Generate Service schema for a program
 */
export function getServiceSchema(program) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": program.title,
    "description": program.description,
    "provider": {
      "@type": "MartialArtsSchool",
      "@id": `${SITE_URL}/#organization`
    },
    "areaServed": {
      "@type": "City",
      "name": "Spring Hill",
      "containedInPlace": {
        "@type": "State",
        "name": "Florida"
      }
    },
    "serviceType": "Martial Arts Training"
  };
}

/**
 * Generate Course schema for a program landing page.
 *
 * Course is the right Google-recognized type for a long-running
 * martial-arts program (better than Service for landing-page intent —
 * Google's Course rich-result eligibility uses provider+name+description).
 */
export function getCourseSchema({ name, description, url, audience, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "url": `${SITE_URL}${url}`,
    ...(image && { "image": image }),
    "provider": {
      "@type": "MartialArtsSchool",
      "@id": `${SITE_URL}/#organization`,
      "name": "Owaza Martial Arts"
    },
    ...(audience && {
      "audience": {
        "@type": "EducationalAudience",
        "audienceType": audience
      }
    }),
    "courseMode": "in-person",
    "inLanguage": "en-US",
    "isAccessibleForFree": false
  };
}

/**
 * Generate all Services schema for homepage
 */
export function getAllServicesSchema() {
  const programs = getStaticData('programs') || [];

  return programs.map(program => getServiceSchema(program));
}

/**
 * Generate BreadcrumbList schema
 */
export function getBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
}

/**
 * Generate WebPage schema
 */
export function getWebPageSchema({ title, description, url }) {
  const pageUrl = `${SITE_URL}${url}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    // Distinct @id so Google's Rich Results validator treats this WebPage as
    // its own entity rather than merging its url into the LocalBusiness
    // detected-items panel (which produces a "Duplicate field 'url'" warning).
    "@id": `${pageUrl}#webpage`,
    "name": title,
    "description": description,
    "url": pageUrl,
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "name": "Owaza Martial Arts",
      "url": SITE_URL
    },
    "about": {
      "@id": `${SITE_URL}/#organization`
    }
  };
}

/**
 * Combine multiple schemas into a single graph
 */
export function combineSchemas(...schemas) {
  // Flatten nested @graph wrappers — if a passed-in schema is already a
  // { @context, @graph: [...] } envelope (e.g. the output of another
  // combineSchemas call), promote its children into the outer @graph instead
  // of nesting an @graph inside an @graph. Nested @graph confuses Google's
  // Rich Results validator (it merges nodes incorrectly, e.g. surfacing the
  // WebPage url alongside the LocalBusiness url as a "duplicate field").
  const flatSchemas = schemas
    .flat()
    .filter(Boolean)
    .flatMap(schema => (Array.isArray(schema['@graph']) ? schema['@graph'] : [schema]));

  if (flatSchemas.length === 0) return null;
  if (flatSchemas.length === 1) return flatSchemas[0];

  return {
    "@context": "https://schema.org",
    "@graph": flatSchemas.map(schema => {
      // Remove @context from individual schemas when combining
      const { "@context": _, ...rest } = schema;
      return rest;
    })
  };
}

/**
 * Get homepage combined schema
 */
export function getHomepageSchema() {
  return combineSchemas(
    getLocalBusinessSchema(),
    getAllServicesSchema(),
    getWebPageSchema({
      title: "Owaza Martial Arts - Jiu-Jitsu & Self-Defense in Spring Hill, FL",
      description: "Expert martial arts training for kids, youth, and adults. Certified self-defense instruction.",
      url: "/"
    })
  );
}

/**
 * Generate Person schema for instructor bio pages
 */
export function getPersonSchema({ name, description, image, jobTitle, url, knowsAbout = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "description": description,
    "url": `${SITE_URL}${url}`,
    ...(image && { "image": image }),
    ...(jobTitle && { "jobTitle": jobTitle }),
    "worksFor": {
      "@type": "MartialArtsSchool",
      "@id": `${SITE_URL}/#organization`,
      "name": "Owaza Martial Arts"
    },
    ...(knowsAbout.length > 0 && { "knowsAbout": knowsAbout })
  };
}

/**
 * Generate AboutPage schema
 */
export function getAboutPageSchema({ title, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": title,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Owaza Martial Arts",
      "url": SITE_URL
    },
    "about": {
      "@type": "MartialArtsSchool",
      "@id": `${SITE_URL}/#organization`
    }
  };
}

/**
 * Generate Article schema for blog/news pages
 */
export function getArticleSchema({ title, description, url, image, datePublished, dateModified, author = "Owaza Martial Arts" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "image": image,
    "datePublished": datePublished,
    "dateModified": dateModified,
    "author": {
      "@type": "Organization",
      "name": author,
      "@id": `${SITE_URL}/#organization`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Owaza Martial Arts",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}${url}`
    }
  };
}

/**
 * Generate FAQPage schema
 */
export function getFAQPageSchema({ title, url, faqs }) {
  const pageUrl = `${SITE_URL}${url}`;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    // Distinct @id so this node stands alone in the @graph rather than
    // collapsing into the LocalBusiness detected-items panel.
    "@id": `${pageUrl}#faqpage`,
    "name": title,
    "url": pageUrl,
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

/**
 * Extract FAQs from HTML content
 * Looks for pattern: <h3>Question?</h3><p>Answer...</p>
 */
export function extractFAQsFromHTML(htmlContent) {
  if (!htmlContent) return [];

  // Simple regex pattern to detect FAQ structure
  const faqPattern = /<h[23][^>]*>(.*?\?)<\/h[23]>\s*<p[^>]*>(.*?)<\/p>/gi;
  const faqs = [];
  let match;

  while ((match = faqPattern.exec(htmlContent)) !== null) {
    faqs.push({
      question: match[1].replace(/<[^>]+>/g, '').trim(),
      answer: match[2].replace(/<[^>]+>/g, '').trim()
    });
  }

  return faqs;
}

/**
 * Event dates are stored as naive Eastern local times ("2026-11-07T09:30:00").
 * Attach the America/New_York offset in effect on that date so search engines
 * don't read them as UTC. Strings that already carry an offset pass through.
 */
export function toEasternISO(dateString) {
  if (!dateString) return null;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(dateString)) return dateString;
  const naive = dateString.split('.')[0];
  let offset = '-05:00';
  try {
    const tzName = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date(`${naive}Z`))
      .find(part => part.type === 'timeZoneName')?.value;
    const match = tzName && tzName.match(/GMT([+-])(\d{1,2})/);
    if (match) offset = `${match[1]}${match[2].padStart(2, '0')}:00`;
  } catch {
    // Keep the EST default if Intl offset names are unavailable
  }
  return `${naive}${offset}`;
}

/**
 * Turn a free-text event location ("Venue, 123 Street, City, FL") into a Place.
 */
function getEventPlace(location) {
  const parts = (location || '').split(',').map(part => part.trim()).filter(Boolean);
  const stateMatch = parts.length >= 3 && parts[parts.length - 1].match(/^([A-Z]{2})(?:\s+(\d{5}))?$/);
  if (!stateMatch) {
    return { "@type": "Place", "name": location, "address": location };
  }
  const city = parts[parts.length - 2];
  const startsWithNumber = /^\d/.test(parts[0]);
  const name = startsWithNumber ? location : parts[0];
  const streetParts = parts.slice(startsWithNumber ? 0 : 1, parts.length - 2);
  return {
    "@type": "Place",
    "name": name,
    "address": {
      "@type": "PostalAddress",
      ...(streetParts.length > 0 && { "streetAddress": streetParts.join(', ') }),
      "addressLocality": city,
      "addressRegion": stateMatch[1],
      ...(stateMatch[2] && { "postalCode": stateMatch[2] }),
      "addressCountry": "US"
    }
  };
}

/**
 * Pull prices out of an event description ("$50 for 3 events, $5 spectator fee"
 * -> ["$50 for 3 events", "$5 spectator fee"]).
 */
export function getEventPriceClauses(event) {
  const priceClauses = (event.description || '').match(/\$\d+(?:\.\d{2})?[^,.;:]*/g) || [];
  return priceClauses.map(clause => clause.trim());
}

/**
 * created_at is a naive UTC timestamp from the API; mark it as UTC.
 */
function toUtcISO(dateString) {
  if (!dateString) return null;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(dateString)) return dateString;
  return `${dateString.split('.')[0]}Z`;
}

function getEventOffers(event, pageUrl) {
  // Pre-registration is by phone, open from the moment the event is published.
  const validFrom = toUtcISO(event.created_at);
  return getEventPriceClauses(event).map(clause => ({
    "@type": "Offer",
    "name": clause,
    "price": clause.match(/\$(\d+(?:\.\d{2})?)/)[1],
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "url": event.registration_url || pageUrl,
    ...(validFrom && { "validFrom": validFrom })
  }));
}

/**
 * Generate Event schema (SportsEvent for tournaments) for a public event
 */
export function getEventSchema(event, url = '/events') {
  const pageUrl = `${SITE_URL}${url}`;
  const offers = getEventOffers(event, pageUrl);
  const owaza = {
    "@type": "Organization",
    "name": "Owaza Martial Arts",
    "url": SITE_URL
  };
  // endDate is deliberately omitted unless set: Owaza doesn't publish an end
  // time, and Google only flags a missing endDate as a non-critical warning.
  return {
    "@context": "https://schema.org",
    "@type": event.event_type === 'tournament' ? "SportsEvent" : "Event",
    "@id": `${pageUrl}#event-${event.id}`,
    "name": event.title,
    ...(event.description && { "description": event.description }),
    "startDate": toEasternISO(event.date),
    ...(event.end_date && { "endDate": toEasternISO(event.end_date) }),
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": getEventPlace(event.location),
    "image": [event.image_url || `${CDN_URL}/images/owaza-og-image.jpg`],
    "url": pageUrl,
    "organizer": owaza,
    "performer": owaza,
    ...(offers.length > 0 && { "offers": offers })
  };
}
